import { subscribeAppForeground } from 'src/modules/appLifecycle';
import type { TutorFocus, TutorSuggestion } from 'src/api/baoTutor';

export type TutorMessage =
  | { id: string; from: 'bao' | 'me'; kind?: 'text'; text: string; suggestions?: TutorSuggestion[] }
  | { id: string; from: 'me'; kind: 'audio'; durationMs: number; blob: Blob };

export interface TutorThread {
  messages: TutorMessage[];
  conversationUuid: string | null;
  hadConversation: boolean;
  isLoading: boolean;
  requestId: number;
  loadingSince: number;
  focus?: TutorFocus | null;
  ended?: boolean;
}

const MAX_THREADS = 10;
export const STALE_REQUEST_MS = 180_000;
let lastRequestId = 0;
const threads = new Map<string, TutorThread>();
const controllers = new Map<number, AbortController>();
const listeners = new Map<string, Set<() => void>>();
const retained = new Map<string, Set<symbol>>();
const opened = new Map<string, Set<symbol>>();
const pendingForgets = new Map<string, string>();

const notify = (key: string) => listeners.get(key)?.forEach(listener => listener());

export const subscribeTutorThread = (key: string, listener: () => void) => {
  const set = listeners.get(key) ?? new Set();
  set.add(listener);
  listeners.set(key, set);
  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(key);
  };
};

export const getTutorThread = (key: string): TutorThread | undefined => threads.get(key);

const hold = (registry: Map<string, Set<symbol>>, key: string) => {
  const token = Symbol(key);
  const holds = registry.get(key) ?? new Set<symbol>();
  holds.add(token);
  registry.set(key, holds);
  return () => {
    const current = registry.get(key);
    if (!current?.delete(token)) return;
    if (current.size > 0) return;
    registry.delete(key);
    if (registry === retained) applyPendingForget(key);
  };
};

export const retainTutorThread = (key: string) => hold(retained, key);

export const markTutorThreadOpen = (key: string) => hold(opened, key);

export const isTutorThreadOnScreen = (key: string) => opened.has(key);

const emptyThread = (): TutorThread => ({
  messages: [], conversationUuid: null, hadConversation: false, isLoading: false, requestId: 0, loadingSince: 0,
});

const cancelRequest = (requestId: number) => {
  controllers.get(requestId)?.abort();
  controllers.delete(requestId);
};

const write = (key: string, thread: TutorThread, { silent = false, touch = true } = {}) => {
  if (touch) threads.delete(key);
  threads.set(key, thread);
  const now = Date.now();
  for (const [candidate, other] of threads) {
    if (threads.size <= MAX_THREADS) break;
    const isPending = other.isLoading && now - other.loadingSince < STALE_REQUEST_MS;
    if (candidate !== key && !retained.has(candidate) && !isPending) {
      if (other.isLoading) cancelRequest(other.requestId);
      threads.delete(candidate);
      notify(candidate);
    }
  }
  if (!silent) notify(key);
};

export const touchTutorThread = (key: string) => {
  const thread = threads.get(key);
  if (thread) write(key, thread, { silent: true });
};

export const updateTutorThread = (
  key: string,
  update: (thread: TutorThread) => Partial<TutorThread>,
  { touch = true } = {},
) => {
  const current = threads.get(key) ?? emptyThread();
  write(key, { ...current, ...update(current) }, { touch });
};

const dropThread = (key: string) => {
  const thread = threads.get(key);
  if (thread?.isLoading) cancelRequest(thread.requestId);
  threads.delete(key);
  notify(key);
};

function applyPendingForget(key: string) {
  const conversationUuid = pendingForgets.get(key);
  pendingForgets.delete(key);
  if (conversationUuid && threads.get(key)?.conversationUuid === conversationUuid) dropThread(key);
}

export const forgetTutorConversation = (conversationUuid: string) => {
  [...threads].forEach(([key, thread]) => {
    if (thread.conversationUuid !== conversationUuid) return;
    if (retained.has(key)) {
      pendingForgets.set(key, conversationUuid);
      return;
    }
    dropThread(key);
  });
};

export const beginTutorRequest = (key: string) => {
  const current = threads.get(key) ?? emptyThread();
  lastRequestId += 1;
  const requestId = lastRequestId;
  const controller = new AbortController();
  controllers.set(requestId, controller);
  write(key, { ...current, isLoading: true, requestId, loadingSince: Date.now() });
  return { requestId, signal: controller.signal };
};

export const settleTutorRequest = (
  key: string,
  requestId: number,
  update: (thread: TutorThread) => Partial<TutorThread>,
) => {
  const current = threads.get(key);
  if (!current || current.requestId !== requestId) return false;
  controllers.delete(requestId);
  write(key, { ...current, ...update(current), isLoading: false, requestId: 0 }, { touch: false });
  return true;
};

export const abandonStaleTutorRequest = (key: string, errorBubble: () => TutorMessage, now = Date.now()) => {
  const current = threads.get(key);
  if (!current?.isLoading || now - current.loadingSince < STALE_REQUEST_MS) return;
  cancelRequest(current.requestId);
  write(key, { ...current, messages: [...current.messages, errorBubble()], isLoading: false, requestId: 0 }, { touch: false });
};

export const endTutorThread = (key: string) => {
  const current = threads.get(key);
  if (!current) return;
  if (current.isLoading) cancelRequest(current.requestId);
  write(key, { ...current, ended: true, isLoading: false, requestId: 0 }, { touch: false });
};

export const dropTutorThread = (key: string) => {
  if (threads.delete(key)) notify(key);
};

export const clearTutorThreads = () => {
  const keys = [...threads.keys()];
  threads.clear();
  pendingForgets.clear();
  [...controllers.keys()].forEach(cancelRequest);
  keys.forEach(notify);
};

export const waitForTutorThreadIdle = (key: string) => new Promise<void>(resolve => {
  if (!threads.get(key)?.isLoading) {
    resolve();
    return;
  }
  const unsubscribe = subscribeTutorThread(key, () => {
    if (threads.get(key)?.isLoading) return;
    unsubscribe();
    resolve();
  });
});

const sweepers = new Set<() => TutorMessage>();
let unsubscribeForeground: (() => void) | undefined;

const sweep = () => {
  const errorBubble = sweepers.values().next().value;
  if (!errorBubble) return;
  const now = Date.now();
  [...threads.keys()].forEach(key => abandonStaleTutorRequest(key, errorBubble, now));
};

export const registerTutorStaleSweep = (errorBubble: () => TutorMessage) => {
  sweepers.add(errorBubble);
  if (sweepers.size === 1) {
    unsubscribeForeground = subscribeAppForeground(sweep, {
      isVisibilityIncluded: true,
    });
  }
  return () => {
    sweepers.delete(errorBubble);
    if (sweepers.size > 0) return;
    unsubscribeForeground?.();
    unsubscribeForeground = undefined;
  };
};
